import { NgClass } from '@angular/common';
import { Component, HostBinding, Input, OnChanges, OnDestroy, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { Option, ParentalControlOption, Plan, QuoteModel } from '@bytel/bytel-sales';
import { QuoteContextRenewalModel, UserOption } from '@bytel/checkout';

import { HandleErrorService } from '@services/handle-error.service';
import { MainCartService } from '@services/main-cart.service';
import { OptionsService } from '@services/options.service';

import { Subject, Subscription, mergeMap, takeUntil } from 'rxjs';

import { DataCyDirective } from '../../../common/directives/data-cy.directive';
import { PriceComponent } from '../../../trilogy/price/price.component';

@Component({
    selector: 'checkout-parental-control',
    templateUrl: './parental-control.component.html',
    styleUrl: 'parental-control.component.scss',
    standalone: true,
    imports: [ReactiveFormsModule, DataCyDirective, NgClass, PriceComponent],
})
export class ParentalControlComponent implements OnChanges, OnDestroy {
    @Input() public cartForm: FormGroup;
    @Input() public plan: Plan;
    @Input() public quoteIndex: number = 0;
    @Input() public isMobile: boolean = false;

    public parentalControlForm: FormGroup<{
        choiceControl: FormControl<boolean>;
        parentalControl: FormControl<string | null>;
    }>;
    public showForm: boolean = false;
    public parentalControlOptions: ParentalControlOption[];
    public options: Option[] = [];
    public waitForCartModifications: boolean = null;

    private selectedOption: Option = null;
    private _subscriptions: Subscription[] = [];

    private $ngUnsubscribe: Subject<void> = new Subject<void>();
    private cartService: MainCartService = inject(MainCartService);
    private formBuilder: FormBuilder = inject(FormBuilder);
    private handleErrorService: HandleErrorService = inject(HandleErrorService);
    private optionService: OptionsService = inject(OptionsService);

    @HostBinding('class.is-block')
    get isBlock(): boolean {
        return this.parentalControlOptions?.length > 0;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.showForm = false;
        const plan = changes.plan;
        if (plan && plan.currentValue) {
            this.optionService
                .getCartOptionsByQuote(this.cartService.cartModel, this.cartService.getQuote(this.quoteIndex))
                .pipe(takeUntil(this.$ngUnsubscribe))
                .subscribe({
                    next: () => this._init(),
                    error: () => this._init(),
                });
        }
    }

    public ngOnDestroy(): void {
        this._subscriptions.forEach((subscription: Subscription) => {
            subscription.unsubscribe();
        });
        this.cartForm.removeControl(this._getFormControlName());
    }

    public setDefaultOption(): void {
        const option: Option = this._getFreeOption() || this._getPremiumOption();
        this.parentalControlForm.get('parentalControl').patchValue(option.gencode, { onlySelf: false });
    }

    public addProduct(option: Option): void {
        this.waitForCartModifications = true;
        this.cartService
            .removeProduct(this.selectedOption, this.quoteIndex)
            .pipe(mergeMap(() => this.cartService.addProduct(option, this.quoteIndex)))
            .subscribe({
                next: () => {
                    this._saveParentalControlChoice();
                    this.selectedOption = option;
                    this.waitForCartModifications = null;
                },
                error: (error) => {
                    this.handleErrorService.handleError(error);
                    this.waitForCartModifications = null;
                },
            });
    }

    public removeProduct(): void {
        this.cartService.removeProduct(this.selectedOption, this.quoteIndex).subscribe(() => {
            this._saveParentalControlChoice();
            this.selectedOption = null;
        });
    }

    private _init(): void {
        this.parentalControlOptions = this._getOptions();
        if (this.parentalControlOptions.length > 0) {
            this._setOptionsFromStorage();

            this.cartForm.addControl(this._getFormControlName(), this.parentalControlForm);

            this._subscriptions.push(
                this.parentalControlForm.get('parentalControl').valueChanges.subscribe((gencode: string) => {
                    const parentalOpt: Option = this.options.find((option) => option.gencode === gencode);
                    if (parentalOpt) {
                        this.addProduct(parentalOpt);
                    } else {
                        this.removeProduct();
                    }
                }),
                this.parentalControlForm.get('choiceControl').valueChanges.subscribe((value: boolean) => {
                    this.removeProduct();
                    this.showForm = value;
                    if (!value) {
                        this.parentalControlForm.get('parentalControl').clearValidators();
                    } else {
                        this.parentalControlForm.get('parentalControl').setValidators([Validators.required]);
                        this.setDefaultOption();
                    }
                }),
            );
        }
    }

    private _getOptions(): ParentalControlOption[] {
        const quote: QuoteModel = this._getQuote();
        if (quote.isRenewal()) {
            const userOptions: UserOption[] = (quote.context as QuoteContextRenewalModel).getData().options;
            const hasParentalOption: boolean = userOptions.some((userOption) =>
                this.optionService.getParentalControlGencodes().includes(userOption.id),
            );

            if (hasParentalOption) {
                return [];
            }
        }

        return this._mapToParentalControlOptions().sort((a, b) => {
            if (a.isPremium && !b.isPremium) {
                return 1;
            }
            if (!a.isPremium && b.isPremium) {
                return -1;
            }
            return 0;
        });
    }

    private _getFreeOption(): Option {
        return this.options.find((option: Option) =>
            this.optionService.getParentalControlFreeGencodes().includes(option.gencode),
        );
    }

    private _getPremiumOption(): Option {
        return this.options.find((option: Option) =>
            this.optionService.getParentalControlPremiumGencodes().includes(option.gencode),
        );
    }

    private _mapToParentalControlOptions(): ParentalControlOption[] {
        if (this.plan) {
            const options: Option[] = this.optionService.getOptionsByPlan(this.plan);
            this.options = this.optionService.filterParentalOptions(options);
            return this.options.map((option: Option) => {
                const isPremium: boolean = this.optionService
                    .getParentalControlPremiumGencodes()
                    .includes(option.gencode);
                return {
                    option,
                    isPremium,
                };
            });
        }
        return [];
    }

    private _saveParentalControlChoice(): void {
        const data = {
            parentalControl: {
                choice: this.parentalControlForm.get('choiceControl').value,
                option: this.parentalControlForm.get('parentalControl').value,
            },
        };
        this._getQuote().addFormData(data);
        this.cartService.save();
    }

    private _setOptionsFromStorage(): void {
        let hasChoiceControl: boolean = false;
        let parentalControl: string;
        const quote: QuoteModel = this._getQuote();
        if (quote) {
            const cartOptions: Option[] = quote.getProductsByType<Option>('Option');
            const selectedParentalControlOption: Option = cartOptions.find((option: Option) =>
                this.optionService.getParentalControlGencodes().includes(option.gencode),
            );
            if (!!selectedParentalControlOption) {
                this.selectedOption = selectedParentalControlOption;
                hasChoiceControl = true;
                parentalControl = this.selectedOption?.gencode;
            } else {
                // @todo handle multiple parental control
                const storedParentalControl = quote?.forms?.parentalControl;
                if (storedParentalControl) {
                    // hasChoiceControl = storedParentalControl.choice;
                    // parentalControl = storedParentalControl.option;
                }
            }
        }

        this.parentalControlForm = this.formBuilder.group({
            choiceControl: hasChoiceControl,
            parentalControl: parentalControl || null,
        });

        this.showForm = hasChoiceControl;
    }

    private _getQuote(): QuoteModel {
        return this.cartService.getQuote(this.quoteIndex);
    }

    private _getFormControlName(): string {
        return `parentalFormControl${this.quoteIndex}`;
    }
}
